import { ArrowUp, ArrowDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Select } from './Select'
import { Button } from './Button'
import type { SelectOption } from './Select'

export interface MobileSortControlProps {
  /** The list of sortable columns to present to the user. */
  options: SelectOption[]
  /** The currently active sort key (matches one of the option values). */
  value: string
  /** Current sort order. */
  order: 'asc' | 'desc'
  /** Called when the user selects a different sort column. */
  onChange: (key: string) => void
  /** Called when the user toggles the sort order. */
  onToggleOrder: () => void
  /** Control density — `sm` matches sm header actions (e.g. the PropertyList
   *  toolbar where it sits beside a sm Add button). Default `md`. */
  size?: 'sm' | 'md'
}

/**
 * EP-97: Mobile sort control — a Select for the sort column + a toggle button
 * for the sort order. Intended to sit inside the existing Filters disclosure
 * on each list page's mobile view.
 *
 * The Select uses the shared Select component (bottom-sheet on mobile).
 * The toggle button uses the shared Button component with an icon.
 */
export function MobileSortControl({
  options,
  value,
  order,
  onChange,
  onToggleOrder,
  size = 'md',
}: MobileSortControlProps) {
  const { t } = useTranslation('common')

  const orderLabel =
    order === 'asc' ? t('sort.order_asc') : t('sort.order_desc')

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 min-w-0">
        <Select
          label={t('sort.label')}
          options={options}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size={size}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size={size}
        aria-label={orderLabel}
        onClick={onToggleOrder}
        className="shrink-0"
      >
        {order === 'asc' ? (
          <ArrowUp className="w-4 h-4" aria-hidden="true" />
        ) : (
          <ArrowDown className="w-4 h-4" aria-hidden="true" />
        )}
        {/* Icon-only on mobile to save width; label returns at `sm+`. The
            aria-label carries the accessible name at every breakpoint. */}
        <span className="hidden sm:inline">{orderLabel}</span>
      </Button>
    </div>
  )
}
